import type { LoadUnit } from './units'

/**
 * Stepper granularity for a DIALLED rack stepper -- the +/- buttons on
 * `WorkoutLogPanel`'s set-weight field (Task 7). Pound plates come in wholes
 * (2.5 / 5 / 10 / 25 / 35 / 45 lb, D4); kg plates step in halves. It used to
 * be declared identically in `WorkoutLogPanel` and `Builder`, each with a
 * comment asserting it matched the other, which is exactly the shape where a
 * divergence goes unnoticed (fix round 1, Minor fold-in) -- but `Builder`'s
 * TYPED target field has since moved to `TARGET_WEIGHT_STEP` below (ruling
 * R16): a dialled stepper and a typed number field are different affordances
 * with different sensible granularities, the same reasoning that already
 * separates this table from `GoalsTab`'s own `TARGET_WEIGHT_BOUNDS`.
 *
 * Not in `units.ts`: that module imports nothing and mirrors `api/platform/units.py` /
 * `Units.swift` symbol-for-symbol (see its own header), and the backend has
 * no display-stepping concern to mirror -- it has no input surfaces. This
 * table is web-input-only.
 *
 * `GoalsTab`'s target-weight field does NOT use either table here -- its own
 * step lives in that file's own `TARGET_WEIGHT_BOUNDS`, on purpose: a
 * body-weight goal and a lifted load are different quantities, not two call
 * sites that happened to duplicate one constant.
 */
export const WEIGHT_STEP: Record<LoadUnit, number> = { kg: 2.5, lb: 5 }

/**
 * Stepper granularity for a TYPED plan target -- `Builder`'s per-exercise
 * weight field, ruling R16 (fix round, I3). `step={WEIGHT_STEP[load]}` on
 * that field was a silent metric behaviour change from the pre-unit-aware
 * `step={0.5}` it replaced: `<input type="number" step="2.5">` marks any
 * existing template target that is not a multiple of 2.5 (81 kg, say) as
 * `stepMismatch`. `weightStep.ts`'s own header reasoning -- different
 * quantities have different sensible granularities -- also separates a
 * DIALLED stepper at the rack (right for `WEIGHT_STEP` above: the increment
 * matches an actual plate change) from a field where fine granularity is the
 * whole point of typing rather than dialling.
 */
export const TARGET_WEIGHT_STEP: Record<LoadUnit, number> = { kg: 0.5, lb: 1 }
